<template>
  <div class="max-w-4xl mx-auto p-6">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Deposit</h1>

      <!-- Navigation/Tab Bar -->
      <div class="flex items-center bg-yellow-100 rounded-lg p-2 mb-6">
        <v-btn icon small class="mr-3" @click="$router.go(-1)">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-gray-800 font-medium">Deposit Crypto</span>
      </div>
    </div>

    <!-- Section Title and Instructions -->
    <div class="mb-8">
      <h2 class="text-xl font-semibold text-gray-800 mb-2">Deposit crypto</h2>
      <p class="text-gray-600">
        Please enter the details below to deposit crypto into your wallet.
      </p>
    </div>

    <!-- Input Fields -->
    <div class="space-y-6">
      <!-- Assets Selection -->
      <div>
        <v-text-field
          v-model="selectedAsset"
          item-text="symbol"
          item-value="symbol"
          label="Selected Asset"
          filled
          rounded
          class="mb-4"
          :loading="loadingAssets"
          readonly
        ></v-text-field>
      </div>

      <!-- Network Selection -->
      <div v-if="availableNetworks.length > 1">
        <v-autocomplete
          v-model="selectedNetwork"
          :items="availableNetworks"
          item-text="name"
          item-value="_id"
          label="Select Network"
          filled
          rounded
          class="mb-4"
          :loading="!walletAddressData"
          :disabled="!walletAddressData"
        ></v-autocomplete>
      </div>
      <!-- Single network: shown statically since there's nothing to choose -->
      <div
        v-else-if="availableNetworks.length === 1"
        class="mb-4 flex items-center bg-gray-50 rounded-lg p-3"
      >
        <span class="text-sm text-gray-600 mr-2">Network:</span>
        <span class="font-medium">{{ availableNetworks[0].name }}</span>
      </div>
      <!-- No network on the wallet yet: say so instead of leaving a dead button -->
      <p
        v-else-if="walletAddressData"
        class="mb-4 text-sm text-red-600"
      >
        No deposit network is available for {{ selectedAsset }} right now.
        Please try again shortly.
      </p>

      <div v-if="minimumDeposit" class="mb-4 text-sm text-gray-600">
        Minimum deposit: {{ minimumDeposit }} {{ selectedAsset }}
      </div>

      <p v-if="!depositIsEnabled" class="text-sm text-red-600">
        Deposits are currently disabled for {{ selectedAsset }}.
      </p>
    </div>

    <div v-if="address">
      <p>
        Scan code to get your {{ selectedNetworkName }} Wallet Address
      </p>
      <div v-if="barcode">
        <img :src="barcode" alt="Deposit address QR code" />
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-sm break-all min-w-0">{{ address }}</span>
        <v-btn icon small class="flex-shrink-0" @click="copy(address)">
          <v-icon>mdi-content-copy</v-icon>
        </v-btn>
      </div>
      <div class="mt-4 bg-accent rounded-md p-4 bg-opacity-40">
        <p>Please note!</p>
        <ul>
          <li>QR code is unique to the token</li>
          <li>
            You can only send <b>{{ selectedAsset }}</b> to this address
          </li>
          <li>
            Sending token other than <b>{{ selectedAsset }}</b> will be results
            in loss of assets
          </li>
          <li>
            Sending <b>{{ selectedAsset }}</b> to a different network will
            result in loss of assets
          </li>
        </ul>
      </div>
    </div>

    <div v-else-if="availableNetworks.length" class="mt-8">
      <p v-if="loadingAddress" class="text-sm text-gray-600 mb-2">
        Generating address… this may take a moment, please wait.
      </p>
      <p v-else class="text-sm text-gray-600 mb-2">
        You have no {{ selectedNetworkName }} address yet.
      </p>
      <div class="flex justify-end">
        <v-btn
          color="primary"
          size="large"
          :disabled="
            !selectedAsset ||
            !selectedNetwork ||
            !depositIsEnabled ||
            loadingAddress
          "
          :loading="loadingAddress"
          @click="generateAddress"
          class="px-8 py-3"
        >
          Generate {{ selectedNetworkName }} Address
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import cryptocoin from '../../../../api/users/cryptocoin'
import { formatNetworkLabel } from '../../../../utils/networkHelpers'

// Address generation is async on the backend: the first call kicks it off and
// returns an empty string, the second one - once the provider has caught up -
// returns the ready address. Same delay the mobile app waits.
const GENERATE_RETRY_DELAY_MS = 5000

export default {
  data() {
    return {
      selectedAsset: '',
      selectedNetwork: '',
      showAssetModal: false,
      assetSearchQuery: '',
      cryptoAssets: [],
      currentAsset: null,
      coinId: '',
      loadingAssets: false,
      walletAddressData: null,
      loadingAddress: false,
      retryTimer: null
    }
  },
  beforeDestroy() {
    if (this.retryTimer) clearTimeout(this.retryTimer)
  },
  computed: {
    // The wallet carries the provider's networks, so only offer the ones
    // deposits are currently open on.
    availableNetworks() {
      return (this.walletAddressData?.networks || [])
        .filter((network) => network.deposits_enabled !== false)
        .map((network) => ({
          name: formatNetworkLabel(network.id, network.name),
          _id: network.id
        }))
    },
    // Each network has its own address; a network that has never been
    // generated has no entry, and a freshly requested one sits there with an
    // empty address until the provider finishes minting it.
    networkAddress() {
      if (!this.walletAddressData || !this.selectedNetwork) return null
      const addresses = this.walletAddressData.addresses || []
      const entry = addresses.find(
        (item) => item.network === this.selectedNetwork
      )
      if (entry) return entry
      // Wallets created before per-network addresses only carry the flat
      // pair, which belongs to the default network.
      if (!addresses.length && this.walletAddressData.address) {
        const fallbackNetwork =
          this.walletAddressData.defaultNetwork || this.selectedNetwork
        if (fallbackNetwork === this.selectedNetwork) {
          return {
            network: this.selectedNetwork,
            address: this.walletAddressData.address,
            barcode: this.walletAddressData.barcode
          }
        }
      }
      return null
    },
    address() {
      return (this.networkAddress && this.networkAddress.address) || ''
    },
    barcode() {
      return (this.networkAddress && this.networkAddress.barcode) || ''
    },
    selectedNetworkName() {
      const network = this.availableNetworks.find(
        (item) => item._id === this.selectedNetwork
      )
      return network ? network.name : ''
    },
    minimumDeposit() {
      return (
        this.walletAddressData &&
        this.walletAddressData.coin &&
        this.walletAddressData.coin.minimumDeposit
      )
    },
    depositIsEnabled() {
      return (
        !this.walletAddressData ||
        !this.walletAddressData.coin ||
        this.walletAddressData.coin.depositEnabled !== false
      )
    }
  },
  watch: {
    // Land on the wallet's default network, or the only one on offer, so the
    // page shows an address without the user having to pick first.
    availableNetworks: {
      immediate: true,
      handler(networks) {
        if (!networks.length) return
        if (networks.some((network) => network._id === this.selectedNetwork)) {
          return
        }
        const preferred = this.walletAddressData?.defaultNetwork
        const match = networks.find((network) => network._id === preferred)
        this.selectedNetwork = (match || networks[0])._id
      }
    }
  },
  mounted() {
    this.coinId = this.$route.params.id
    this.getCryptoAddress()
    this.getCryptoAssets()
  },
  methods: {
    async getCryptoAssets() {
      try {
        this.loadingAssets = true
        const res = await cryptocoin.cryptoCoin().getCryptosRate()
        const currentCoin = res.data.data.find(
          (asset) => asset.coinId === this.coinId
        )

        if (currentCoin) {
          this.cryptoAssets = [
            {
              symbol: currentCoin.symbol,
              name: currentCoin.currency,
              logo: currentCoin.logo,
              price: currentCoin.price,
              percentageChange: currentCoin.percentageChange,
              coinId: currentCoin.coinId
            }
          ]
          this.loadingAssets = false

          this.selectAsset(this.cryptoAssets[0])
        }
      } catch (error) {
        console.log('Error fetching crypto assets:', error)
      } finally {
        this.loadingAssets = false
      }
    },
    selectAsset(asset) {
      this.currentAsset = asset
      this.selectedAsset = asset.symbol
      this.showAssetModal = false
      this.assetSearchQuery = ''
    },
    async generateAddress() {
      if (this.address) return
      if (!this.depositIsEnabled) {
        this.alertUser(
          `Deposits are currently disabled for ${this.selectedAsset}`
        )
        return
      }
      if (!this.selectedAsset || !this.selectedNetwork) {
        this.alertUser('Please select an asset and network')
        return
      }

      const payload = { coinId: this.coinId, network: this.selectedNetwork }
      this.loadingAddress = true

      const first = await cryptocoin.cryptoCoin().generateCryptoAddress(payload)
      if (first.error) {
        this.alertUser(this.apiErrorMessage(first))
        this.loadingAddress = false
        return
      }

      // The first call only starts generation, so give the provider a moment
      // and ask again - the second response carries the address.
      this.retryTimer = setTimeout(async () => {
        const second = await cryptocoin
          .cryptoCoin()
          .generateCryptoAddress(payload)
        if (second.error) {
          this.alertUser(this.apiErrorMessage(second))
          this.loadingAddress = false
          return
        }
        await this.getCryptoAddress()
        this.loadingAddress = false
        if (!this.address) {
          this.alertUser(
            'Your address is still being generated. Please check back shortly.'
          )
        }
      }, GENERATE_RETRY_DELAY_MS)
    },
    alertUser(message, error = true) {
      this.$store.dispatch('alert', { message, error, status: true })
    },
    apiErrorMessage(res) {
      const em = res && res.errorMessage
      if (typeof em === 'string' && em.trim()) return em
      if (em && typeof em === 'object' && typeof em.message === 'string') {
        return em.message
      }
      return 'Could not generate an address. Please try again.'
    },
    async getCryptoAddress() {
      const res = await cryptocoin.cryptoCoin().getCryptosWallets()
      if (res.error) {
        this.$store.dispatch('alert', {
          message: res.message,
          error: true,
          status: true
        })
        return
      }

      const wallet = res.data.data.find(
        (wallet) => wallet.coin._id === this.coinId
      )
      this.walletAddressData = wallet
      if (wallet?._id) {
        const fresh = await cryptocoin.cryptoCoin().getCryptoWallet(wallet._id)
        if (!fresh.error && fresh.data?.data) {
          this.walletAddressData = fresh.data.data
        }
      }
    },
    copy(address) {
      navigator.clipboard.writeText(address)
      this.$store.dispatch('alert', {
        message: 'Address copied to clipboard',
        error: false,
        status: true
      })
    }
  }
}
</script>

<style scoped>
/* Custom styles for the purple button */
.v-btn--color-purple {
  background-color: #8b5cf6 !important;
  color: white !important;
}

.v-btn--color-purple:hover {
  background-color: #7c3aed !important;
}

.v-btn--color-purple:disabled {
  background-color: #d1d5db !important;
  color: #9ca3af !important;
}
</style>
